import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

type ModelLine = { code: string; desc: string };
type Suffix = { code: string; meaning: string };
type PowerRow = { code: string; cv: string };
type ExamplePart = { text: string; label: string };
type Example = { model: string; parts: ExamplePart[] };

type Brand = {
  id: string;
  name: string;
  logo: string;
  logoSrc: string;
  color: string;
  description: string;
  formula?: string;
  modelLines: ModelLine[];
  modelLinesNote?: string;
  extraModelLines?: ModelLine[];
  examples?: Example[];
  numberNote?: string;
  suffixes?: Suffix[];
  suffixWarning?: string;
  hierarchy?: string[];
  note?: string;
  powerTable?: PowerRow[];
};

type BrandGroup = {
  label: string;
  brands: Brand[];
};

const brandGroups: BrandGroup[] = [
  {
    label: "Alemanyes",
    brands: [
      {
        id: "bmw",
        name: "BMW",
        logo: "B",
        logoSrc: "/logos/bmw-logo.png",
        color: "#0066CC",
        description: "Un dels sistemes més lògics. El nom segueix sempre la mateixa estructura.",
        formula: "[Sèrie]  [Motorització]  [Tracció]  [Variant]",
        modelLines: [
          { code: "1 Sèrie", desc: "Compacte, el més petit" },
          { code: "2 Sèrie", desc: "Coupé/cabrio compacte" },
          { code: "3 Sèrie", desc: "Berlina mitjana (la icònica)" },
          { code: "4 Sèrie", desc: "Coupé/cabrio de la 3" },
          { code: "5 Sèrie", desc: "Berlina executiva" },
          { code: "7 Sèrie", desc: "Berlina de luxe màxim" },
          { code: "X1, X3, X5…", desc: "SUVs — el número indica mida" },
          { code: "Z4", desc: "Roadster esportiu" },
          { code: "M2, M3, M4…", desc: "Versions de competició" },
        ],
        examples: [
          {
            model: "420i",
            parts: [
              { text: "4", label: "Sèrie 4 — coupé derivat de la 3" },
              { text: "20", label: "Nivell potència baix" },
              { text: "i", label: "Gasolina" },
            ],
          },
          {
            model: "430d xDrive",
            parts: [
              { text: "4", label: "Sèrie 4" },
              { text: "30", label: "Nivell potència mig" },
              { text: "d", label: "Dièsel" },
              { text: "xDrive", label: "Tracció total" },
            ],
          },
        ],
        numberNote:
          "El número darrere (ex: 420, 430, 440) indicava la cilindrada, però ara indica el nivell de potència dins la sèrie. El 430i no té necessàriament 3.0L.",
        suffixes: [
          { code: "i", meaning: "Gasolina (injection)" },
          { code: "d", meaning: "Dièsel" },
          { code: "e", meaning: "Plug-in hybrid" },
          { code: "xDrive", meaning: "Tracció total" },
          { code: "sDrive", meaning: "Tracció posterior" },
          { code: "M…", meaning: "Versió de competició pura (M3, M4…)" },
          { code: "M Sport", meaning: "Pack estètic esportiu" },
        ],
        suffixWarning: "M davant ≠ M Sport. M Sport és només estètica, M és un cotxe completament diferent.",
      },
      {
        id: "mercedes-benz",
        name: "Mercedes-Benz",
        logo: "M",
        logoSrc: "/logos/mercedes-benz-logo.png",
        color: "#1a1a1a",
        description: "Classe (lletra) + número de potència relativa + sufixos. Els SUVs afegeixen el prefix GL.",
        formula: "Classe  +  Número  +  Sufixos",
        modelLines: [
          { code: "A", desc: "Compacte, el més petit i assequible" },
          { code: "C", desc: "Berlina mitjana" },
          { code: "E", desc: "Berlina executiva" },
          { code: "S", desc: "Berlina de luxe suprem (Sonderklasse)" },
          { code: "G", desc: "G-Wagen, tot terreny llegendari" },
        ],
        modelLinesNote: "SUVs (prefix GL, homenatge al G-Wagen):",
        extraModelLines: [
          { code: "GLA", desc: "SUV compacte (base A-Class)" },
          { code: "GLC", desc: "SUV mitjà (base C-Class)" },
          { code: "GLE", desc: "SUV gran (base E-Class)" },
          { code: "GLS", desc: "SUV de luxe màxim" },
          { code: "CLA", desc: "Fastback entre A i C" },
          { code: "CLS", desc: "Berlina coupé executiva" },
          { code: "SL", desc: "Roadster de luxe (Sport Leicht)" },
          { code: "AMG GT", desc: "Supersportiu pur" },
        ],
        examples: [
          {
            model: "C220d",
            parts: [
              { text: "C", label: "Classe C — berlina mitjana" },
              { text: "220", label: "Potència relativa ~194 CV" },
              { text: "d", label: "Dièsel" },
            ],
          },
          {
            model: "GLC 300 4MATIC",
            parts: [
              { text: "GLC", label: "SUV mida C-Class" },
              { text: "300", label: "Potència relativa ~258 CV" },
              { text: "4MATIC", label: "Tracció total" },
            ],
          },
        ],
        numberNote:
          "El número (ex: E220, C300) indicava la cilindrada, però des de ~2015 indica potència relativa, no cilindrada real. Un C300 no té 3.0L.",
        suffixes: [
          { code: "d", meaning: "Dièsel" },
          { code: "e", meaning: "Plug-in hybrid" },
          { code: "4MATIC", meaning: "Tracció total" },
          { code: "AMG 43/53/63", meaning: "Versió esportiva (potència creixent)" },
          { code: "Maybach", meaning: "Ultra-luxe, per sobre del S-Class" },
          { code: "EQ", meaning: "Gamma elèctrica (EQA, EQB, EQS…)" },
        ],
      },
      {
        id: "audi",
        name: "Audi",
        logo: "A",
        logoSrc: "/logos/audi-logo.png",
        color: "#BB0A30",
        description: "Dos sistemes paral·lels: A-series per a cotxes, Q-series per a SUVs. Amb un codi de potència propi.",
        formula: "Lletra  +  Mida  +  Codi potència  +  Motorització",
        modelLines: [
          { code: "A1", desc: "El més petit" },
          { code: "A3", desc: "Compacte" },
          { code: "A4", desc: "Berlina mitjana" },
          { code: "A5 / A7", desc: "Coupés (parells = coupé/cabrio)" },
          { code: "A6", desc: "Executiu" },
          { code: "A8", desc: "Màxim luxe" },
        ],
        modelLinesNote: "SUVs (Q-series) i esportius:",
        extraModelLines: [
          { code: "Q2, Q3", desc: "SUVs compactes" },
          { code: "Q5", desc: "SUV mida mitjana" },
          { code: "Q7, Q8", desc: "SUVs grans" },
          { code: "TT", desc: "Coupé/roadster icònic" },
          { code: "R8", desc: "Superesportiu" },
          { code: "S3, S4, S6…", desc: "Versió esportiva intermèdia" },
          { code: "RS3, RS6…", desc: "Versió de competició extrema" },
          { code: "e-tron", desc: "Versió elèctrica" },
        ],
        examples: [
          {
            model: "A4 40 TDI quattro",
            parts: [
              { text: "A4", label: "Berlina gamma mitjana" },
              { text: "40", label: "~170–200 CV (codi propi)" },
              { text: "TDI", label: "Dièsel turbo" },
              { text: "quattro", label: "Tracció total" },
            ],
          },
          {
            model: "RS6 Avant",
            parts: [
              { text: "RS", label: "Racing Sport — màxima potència" },
              { text: "6", label: "Basat en A6" },
              { text: "Avant", label: "Carrosseria familiar" },
            ],
          },
        ],
        numberNote:
          "Audi afegeix un codi de dues xifres que indica la potència en una escala pròpia — no la cilindrada. Exemple: A4 40 TDI ≠ 4.0L.",
        powerTable: [
          { code: "30", cv: "~115–150 CV" },
          { code: "35", cv: "~150–165 CV" },
          { code: "40", cv: "~170–200 CV" },
          { code: "45", cv: "~230–250 CV" },
          { code: "50", cv: "~285–310 CV" },
          { code: "55", cv: "~330–370 CV" },
        ],
        suffixes: [
          { code: "TFSI", meaning: "Gasolina turbo injecció directa" },
          { code: "TDI", meaning: "Dièsel turbo" },
          { code: "quattro", meaning: "Tracció total (terme registrat d'Audi)" },
          { code: "S line", meaning: "Pack estètic esportiu (no és versió S)" },
          { code: "g-tron", meaning: "Gas natural" },
        ],
      },
      {
        id: "porsche",
        name: "Porsche",
        logo: "P",
        logoSrc: "/logos/porsche-logo.png",
        color: "#C0272D",
        description: "Noms propis i números icònics sense un sistema tan lineal. Cada model té la seva pròpia jerarquia.",
        formula: "Nom del model  +  Variant de potència",
        modelLines: [
          { code: "911", desc: "L'esportiu llegendari (des del 1963 — originalment 901, però Peugeot tenia la patent dels noms amb 0 al mig)" },
          { code: "718 Boxster", desc: "Esportiu dos places, cabrio" },
          { code: "718 Cayman", desc: "Esportiu dos places, coupé" },
          { code: "Cayenne", desc: "SUV gran (nom = pebre picant, suggereix agressivitat)" },
          { code: "Macan", desc: "SUV compacte (nom = tigre en indonesi)" },
          { code: "Panamera", desc: "Berlina esportiva (nom = Carretera Panamericana)" },
          { code: "Taycan", desc: "Elèctric (nom = ànima d'un cavall jove en turc)" },
        ],
        examples: [
          {
            model: "911 Carrera S",
            parts: [
              { text: "911", label: "Model icònic" },
              { text: "Carrera", label: "Versió base — ja molt potent" },
              { text: "S", label: "Sport, més potent" },
            ],
          },
          {
            model: "Cayenne GTS",
            parts: [
              { text: "Cayenne", label: "SUV gran" },
              { text: "GTS", label: "Gran Turisme Sport" },
            ],
          },
        ],
        numberNote:
          "El GT3 és diferent de tota la gamma Turbo: sense turbocompressor, dissenyat per circuit. En elèctrics com el Taycan, 'Turbo' indica la versió de màxima potència — sense turbo real.",
        hierarchy: ["Carrera", "S", "GTS", "Turbo", "Turbo S"],
        suffixes: [
          { code: "Carrera", meaning: "Base del 911" },
          { code: "S", meaning: "Sport — més potent" },
          { code: "GTS", meaning: "Gran Turismo Sport — entre S i Turbo" },
          { code: "Turbo", meaning: "Màxima potència" },
          { code: "Turbo S", meaning: "La versió absoluta" },
          { code: "GT3 / GT3 RS", meaning: "Competició sense turbo, per circuit" },
          { code: "Targa", meaning: "Sostre semi-obert amb franja metàl·lica" },
          { code: "Cabriolet", meaning: "Totalment descapotable" },
          { code: "4 / 4S", meaning: "Tracció integral" },
        ],
      },
      {
        id: "volkswagen",
        name: "Volkswagen",
        logo: "V",
        logoSrc: "/logos/volkswagen-logo.png",
        color: "#0A4A8A",
        description: "Noms propis per a berlinas, prefix T per a SUVs, ID per a elèctrics. El Golf és la referència del segment.",
        formula: "Nom propi / T + Número (SUV) / ID.Número (elèctric)",
        modelLines: [
          { code: "Polo", desc: "Compacte petit" },
          { code: "Golf", desc: "Compacte icònic (el més venut d'Europa durant dècades)" },
          { code: "Passat", desc: "Berlina/familiar gran" },
          { code: "T-Cross", desc: "SUV petit" },
          { code: "T-Roc", desc: "SUV compacte" },
          { code: "Tiguan", desc: "SUV mida mitjana" },
          { code: "Touareg", desc: "SUV gran de luxe" },
          { code: "Arteon", desc: "Fastback premium" },
          { code: "ID.3", desc: "Elèctric compacte" },
          { code: "ID.4", desc: "SUV elèctric" },
          { code: "ID.5", desc: "SUV coupé elèctric" },
          { code: "ID.7", desc: "Berlina elèctrica gran" },
        ],
        suffixes: [
          { code: "TSI", meaning: "Gasolina turbo injecció directa" },
          { code: "TDI", meaning: "Dièsel turbo" },
          { code: "GTE", meaning: "Plug-in hybrid esportiu" },
          { code: "GTI", meaning: "Gran Turisme Injection — versió esportiva icònica" },
          { code: "GTD", meaning: "Golf dièsel esportiu" },
          { code: "R", meaning: "Versió de màxima potència (Golf R, 333 CV)" },
          { code: "4Motion", meaning: "Tracció total" },
          { code: "R-Line", meaning: "Pack estètic esportiu" },
        ],
        note: "El GTI és una llegenda des del 1974 (Gran Turisme Injection). L'R (Golf R) és la versió extrema. 4Motion és la tracció total compartida amb tot el Grup VW.",
      },
      {
        id: "opel",
        name: "Opel",
        logo: "O",
        logoSrc: "/logos/opel-logo.png",
        color: "#E5A800",
        description: "Noms propis evocadors. Cada model té nom propi, sense sistema numèric. Filial del grup Stellantis.",
        formula: "Nom propi",
        modelLines: [
          { code: "Corsa", desc: "Compacte petit" },
          { code: "Astra", desc: "Compacte" },
          { code: "Mokka", desc: "SUV compacte" },
          { code: "Grandland", desc: "SUV gran" },
          { code: "Frontera", desc: "SUV nou assequible" },
          { code: "Combo", desc: "Furgoneta compacte" },
        ],
        suffixes: [
          { code: "GSi", meaning: "Gran Sport Injection — versió esportiva intermèdia" },
          { code: "GSe", meaning: "Gran Sport Electric — versió híbrida esportiva" },
          { code: "OPC", meaning: "Opel Performance Center — versió de competició extrema" },
          { code: "e-", meaning: "Versió 100% elèctrica (ex: Corsa-e)" },
        ],
      },
      {
        id: "mini",
        name: "Mini",
        logo: "M",
        logoSrc: "/logos/mini-logo.png",
        color: "#1C1C1C",
        description: "La MINI és una icona britànica sota propietat BMW. Gamma amb variants nomenades per la mida/ús.",
        formula: "Mini + Variant de cos",
        modelLines: [
          { code: "Cooper", desc: "Hatchback icònic de 3 portes" },
          { code: "Cooper 5 portes", desc: "Hatchback familiar" },
          { code: "Convertible", desc: "Cabrio" },
          { code: "Countryman", desc: "SUV gran" },
          { code: "Aceman", desc: "Nou SUV compacte elèctric" },
        ],
        suffixes: [
          { code: "One", meaning: "Nivell base (descontinuat)" },
          { code: "Cooper", meaning: "Nivell estàndard" },
          { code: "S", meaning: "Sport — potència augmentada" },
          { code: "JCW", meaning: "John Cooper Works — màxima potència (~230 CV)" },
          { code: "SE / E", meaning: "Versió elèctrica" },
        ],
        note: "JCW (John Cooper Works) és la versió de competició. John Cooper va dissenyar el Mini de competició original als anys 60. El Cooper S és el punt dolç entre prestacions i quotidianitat.",
      },
      {
        id: "smart",
        name: "Smart",
        logo: "S",
        logoSrc: "/logos/smart-logo.png",
        color: "#00ADEF",
        description: "Originalment el micro-urbà ForTwo. Ara joint venture Geely+Mercedes amb gamma #1, #3, #5 d'SUVs elèctrics.",
        formula: "#Número (nou) / ForTwo (clàssic)",
        modelLines: [
          { code: "Smart #1", desc: "SUV compacte elèctric" },
          { code: "Smart #3", desc: "SUV coupé elèctric" },
          { code: "Smart #5", desc: "SUV gran elèctric" },
        ],
        suffixes: [
          { code: "Brabus", meaning: "Versió esportiva d'alta potència (marca tuner alemanya)" },
          { code: "Pro / Pro+", meaning: "Nivells d'equipament" },
        ],
        note: "Smart va ser creada per Mercedes-Benz i Swatch el 1994 per a la mobilitat urbana. El 2019 va fer joint venture amb Geely (Xina). Ara fabrica exclusivament elèctrics.",
      },
      {
        id: "skoda",
        name: "Škoda",
        logo: "Š",
        logoSrc: "/logos/skoda-logo.png",
        color: "#4BA82E",
        description: "Filial de Volkswagen des del 1991. Noms eslavos evocadors de natura, llocs i conceptes. La marca assequible del Grup VW.",
        formula: "Nom propi (origen eslau o natural)",
        modelLines: [
          { code: "Fabia", desc: "Compacte petit" },
          { code: "Octavia", desc: "Compacte/familiar — la més venuda de Škoda" },
          { code: "Superb", desc: "Berlina gran" },
          { code: "Kamiq", desc: "SUV compacte petit" },
          { code: "Karoq", desc: "SUV compacte" },
          { code: "Kodiaq", desc: "SUV gran (nom d'un os grizzly)" },
          { code: "Enyaq", desc: "SUV elèctric (nom gaèlic d'origen irlandès)" },
          { code: "Elroq", desc: "SUV compacte elèctric" },
        ],
        suffixes: [
          { code: "TSI", meaning: "Gasolina turbo (plataforma VW compartida)" },
          { code: "TDI", meaning: "Dièsel turbo" },
          { code: "iV", meaning: "Plug-in hybrid" },
          { code: "RS", meaning: "Versió esportiva extrema (Octavia RS, Kodiaq RS)" },
          { code: "Scout", meaning: "Versió off-road/aventura (Octavia Scout)" },
        ],
        note: "Škoda comparteix plataformes, motors i sufixos amb Volkswagen i Audi. L'Octavia és un dels cotxes amb millor relació qualitat-preu d'Europa.",
      },
    ],
  },
  {
    label: "Franceses",
    brands: [
      {
        id: "peugeot",
        name: "Peugeot",
        logo: "P",
        logoSrc: "/logos/peugeot-logo.png",
        color: "#002D5E",
        description: "Sistema numèric amb 0 al mig: X0X per a berlinas, X00X per a SUVs. Un dels sistemes més coherents del mercat.",
        formula: "X0X (berlina) / X00X (SUV)",
        modelLines: [
          { code: "208", desc: "Compacte petit" },
          { code: "308", desc: "Compacte" },
          { code: "408", desc: "Fastback" },
          { code: "508", desc: "Berlina executiva" },
          { code: "2008", desc: "SUV petit" },
          { code: "3008", desc: "SUV mida mitjana" },
          { code: "5008", desc: "SUV gran 7 places" },
          { code: "e-208 / e-2008", desc: "Versions 100% elèctriques" },
        ],
        suffixes: [
          { code: "e-", meaning: "Versió 100% elèctrica (e-208, e-2008, e-308)" },
          { code: "GT / GT Line", meaning: "Versió esportiva / pack estètic esportiu" },
          { code: "Allure", meaning: "Equipament alt" },
          { code: "Active", meaning: "Equipament base" },
        ],
        note: "Peugeot va registrar el sistema de noms amb '0' al mig per protegir-se de BMW. Els SUVs afegeixen un dígit: 2008, 3008, 5008 (el 4 indica la família, 00 que és SUV, 8 el rang).",
      },
      {
        id: "renault",
        name: "Renault",
        logo: "R",
        logoSrc: "/logos/renault-logo.png",
        color: "#FFCC00",
        description: "Noms propis inspirats en vents, llocs i conceptes. L'aliança Renault-Nissan-Mitsubishi és el grup automobilístic més gran del món.",
        formula: "Nom propi",
        modelLines: [
          { code: "Clio", desc: "Compacte petit icònic (el més venut de França)" },
          { code: "Megane", desc: "Compacte" },
          { code: "Scenic", desc: "SUV compacte nou" },
          { code: "Austral", desc: "SUV mida mitjana" },
          { code: "Arkana", desc: "SUV coupé" },
          { code: "Koleos", desc: "SUV gran" },
          { code: "Espace", desc: "Monovolum-SUV" },
          { code: "Zoe", desc: "Elèctric compacte icònic" },
          { code: "Megane E-Tech", desc: "Compacte elèctric" },
          { code: "R5 E-Tech", desc: "Revival del Renault 5 clàssic (elèctric)" },
        ],
        suffixes: [
          { code: "E-Tech", meaning: "Hibridació (mild, full) o elèctric pur" },
          { code: "RS", meaning: "Renault Sport — versió de competició" },
          { code: "Nismo", meaning: "Col·laboració amb Nissan Motorsport" },
        ],
        note: "El Renault 5 E-Tech és el revival del mític R5 dels anys 80. Renault i Nissan formen l'Aliança Renault-Nissan-Mitsubishi, que inclou Dacia, Alpine i altres.",
      },
      {
        id: "citroen",
        name: "Citroën",
        logo: "C",
        logoSrc: "/logos/citroen-logo.png",
        color: "#C8102E",
        description: "Berlinas amb C + número, models de niche amb noms propis. Marca francesa amb fort caràcter i suspensió hidràulica llegendària.",
        formula: "C + Número / Nom propi",
        modelLines: [
          { code: "C3", desc: "Compacte petit" },
          { code: "C4", desc: "Compacte" },
          { code: "C5 X", desc: "SUV executiu" },
          { code: "Berlingo", desc: "Furgoneta d'oci familiar" },
          { code: "SpaceTourer", desc: "Furgoneta gran" },
          { code: "Ami", desc: "Micro-vehicle elèctric (es pot conduir des dels 14 anys a França)" },
          { code: "ë-C3", desc: "C3 elèctric" },
        ],
        suffixes: [
          { code: "ë-", meaning: "Versió 100% elèctrica (ë-C3, ë-Berlingo)" },
          { code: "Feel / Shine", meaning: "Nivells d'equipament creixents" },
        ],
        note: "El Citroën 2CV i la DS dels anys 50 van revolutionar el disseny automobilístic. El Ami és el vehicle urbà elèctric més barat d'Europa i es pot conduir sense carnet a França.",
      },
      {
        id: "ds",
        name: "DS Automobiles",
        logo: "D",
        logoSrc: "/logos/ds-logo.png",
        color: "#9B1C31",
        description: "Sub-marca premium de Citroën. Tots els models porten DS + número. Nomenada en homenatge a la Déesse ('Deessa' en francès), l'icònic DS de 1955.",
        formula: "DS + Número",
        modelLines: [
          { code: "DS3", desc: "Compacte premium" },
          { code: "DS4", desc: "Fastback premium" },
          { code: "DS7", desc: "SUV premium mida mitjana" },
          { code: "DS9", desc: "Berlina gran premium" },
        ],
        suffixes: [
          { code: "E-Tense", meaning: "Versió elèctrica o PHEV" },
          { code: "Performance Line", meaning: "Pack esportiu" },
          { code: "La Première", meaning: "Màxima especificació" },
        ],
        note: "DS Automobiles es va independitzar de Citroën el 2015. El nom és homenatge al Citroën DS de 1955, un dels cotxes més revolucionaris de la història (suspensió hidropneumàtica, dissenys futuristes).",
      },
      {
        id: "alpine",
        name: "Alpine",
        logo: "A",
        logoSrc: "/logos/alpine-logo.png",
        color: "#0055A5",
        description: "Marca esportiva ressuscitada de Renault. A + número, com la tradició dels anys 60-70. L'A110 és una obra mestre de la lleugeresa.",
        formula: "A + Número",
        modelLines: [
          { code: "A110", desc: "Esportiu lleüger icònic — 2 places, motor central" },
          { code: "A290", desc: "Hatchback elèctric esportiu (basat en Renault 5)" },
          { code: "A390", desc: "Berlina/fastback esportiva elèctrica (propera)" },
        ],
        note: "L'A110 és una obra mestre: menys de 1.100 kg, motor central, comportament pur. Renault va ressuscitar Alpine el 2017 amb el llegendari A110. La nova gamma elèctrica manté l'esperit esportiu.",
      },
      {
        id: "dacia",
        name: "Dacia",
        logo: "D",
        logoSrc: "/logos/dacia-logo.png",
        color: "#005C8E",
        description: "Filial de Renault de Romania. Noms propis assequibles. La marca que ha fet els cotxes nous accessibles per a tothom.",
        formula: "Nom propi",
        modelLines: [
          { code: "Sandero", desc: "El cotxe nou més barat d'Europa" },
          { code: "Duster", desc: "SUV asequible tot-terreny (icona de la marca)" },
          { code: "Logan", desc: "Berlina familiar econòmica" },
          { code: "Jogger", desc: "Familiar 7 places assequible" },
          { code: "Spring", desc: "Micro-SUV elèctric — el cotxe elèctric nou més barat d'Europa" },
          { code: "Bigster", desc: "SUV gran nou (2025)" },
        ],
        note: "Dacia Spring és l'elèctric nou més barat d'Europa (~17.000€). El Duster és una icona: combina capacitat real off-road amb un preu excepcional. Dacia és part del grup Renault.",
      },
    ],
  },
  {
    label: "Italianes",
    brands: [
      {
        id: "ferrari",
        name: "Ferrari",
        logo: "F",
        logoSrc: "/logos/ferrari-logo.png",
        color: "#CC0000",
        description: "Noms propis evocadors i codis alfanumèrics per a especificacions. El número pot indicar cilindrada, potència o una efeméride especial.",
        formula: "Nom propi / Codi + Número",
        modelLines: [
          { code: "Roma", desc: "GT coupé elegant" },
          { code: "SF90 Stradale", desc: "PHEV supercar (SF90 = Scuderia Ferrari 90è aniversari)" },
          { code: "296 GTB", desc: "V6 PHEV 830 CV" },
          { code: "F8 Tributo", desc: "V8 biturbo" },
          { code: "812 Superfast", desc: "V12 aspirat 800 CV" },
          { code: "Purosangue", desc: "El primer 'SUV' de Ferrari (4 places)" },
          { code: "499P", desc: "Hypercar de competició Le Mans" },
        ],
        numberNote: "SF90 = 90è aniversari de la Scuderia Ferrari. 296 = 2.9L V6. 812 = 800 CV / 12 cilindres. El número amaga sempre una referència tècnica o histórica.",
        suffixes: [
          { code: "GTB", meaning: "Gran Turisme Berlinette — coupé" },
          { code: "GTS", meaning: "Gran Turisme Spider — cabrio" },
          { code: "Spider", meaning: "Descapotable" },
          { code: "Stradale", meaning: "De carretera (vs competició pura)" },
          { code: "Tributo", meaning: "Tribut a versions anteriors" },
          { code: "XX", meaning: "Versió de pista extrema (LaFerrari XX)" },
        ],
      },
      {
        id: "lamborghini",
        name: "Lamborghini",
        logo: "L",
        logoSrc: "/logos/lamborghini-logo.png",
        color: "#C8A800",
        description: "Noms de braus de combat famosos o forces de la natura. Potència extrema amb personalitat brutal.",
        formula: "Nom propi (brau / força natural)",
        modelLines: [
          { code: "Huracán", desc: "Descontinuat 2024 — nom d'un huracà / brau de combat" },
          { code: "Temerario", desc: "Nou 2024, substitueix Huracán" },
          { code: "Revuelto", desc: "V12 PHEV 1.001 CV, substitueix Aventador" },
          { code: "Urus", desc: "SUV — nom del bou salvatge extingit" },
        ],
        suffixes: [
          { code: "Performante", meaning: "Versió de màxima prestació (pista)" },
          { code: "STO", meaning: "Super Trofeo Omologata — competició homologada" },
          { code: "EVO", meaning: "Evolució del model" },
          { code: "LP…", meaning: "Longitudinale Posteriore + potència en CV" },
        ],
        note: "Tots els models Lamborghini porten noms de braus de combat cèlebres o forces naturals extremes: Huracán (tornada), Aventador (brau de combat), Urus (bou extingit), Revuelto (agitat/rebel).",
      },
      {
        id: "alfa-romeo",
        name: "Alfa Romeo",
        logo: "A",
        logoSrc: "/logos/alfa-romeo-logo.png",
        color: "#C41230",
        description: "Noms propis italians clàssics, molts de llocs alpins. Ara amb una gamma SUV moderna. El 'trèvol de 4 fulles' és el símbol esportiu.",
        formula: "Nom italià / Referència alpina",
        modelLines: [
          { code: "Junior", desc: "SUV petit elèctric/hybrid" },
          { code: "Tonale", desc: "SUV compacte (Passo del Tonale, coll alpí)" },
          { code: "Stelvio", desc: "SUV mida mitjana (Passo dello Stelvio)" },
          { code: "Giulia", desc: "Berlina (nom femení italià)" },
          { code: "33 Stradale", desc: "Supercar de col·lecció, revival del llegendari 33" },
        ],
        suffixes: [
          { code: "Quadrifoglio", meaning: "Trèvol de 4 fulles — versió de competició màxima (V6 Biturbo 520 CV)" },
          { code: "Veloce", meaning: "Ràpid — versió esportiva intermèdia" },
          { code: "Sprint", meaning: "Pack esportiu" },
          { code: "PHEV", meaning: "Plug-in hybrid" },
        ],
        note: "El Quadrifoglio és la versió extrema: el distintiu del trèvol va néixer als anys 20 com a porta-sort de Ugo Sivocci, pilot d'Alfa Romeo que va guanyar amb ell. La Giulia Quadrifoglio té un V6 2.9L biturbo de 510 CV.",
      },
      {
        id: "maserati",
        name: "Maserati",
        logo: "M",
        logoSrc: "/logos/maserati-logo.png",
        color: "#00205B",
        description: "Noms propis italians i grecs. El trident és el símbol de Neptú, de Bolonya (on hi ha la Font de Neptú). Velocitat i elegància italiana.",
        formula: "Nom propi (vent / concepte poètic)",
        modelLines: [
          { code: "Grecale", desc: "SUV compacte (vent del Mediterrani)" },
          { code: "Levante", desc: "SUV gran (vent de llevant)" },
          { code: "Quattroporte", desc: "Berlina gran (= 'quatre portes' en italià)" },
          { code: "Ghibli", desc: "Berlina (vent càlid d'Àfrica)" },
          { code: "GranTurismo", desc: "GT coupé" },
          { code: "GranCabrio", desc: "GT cabrio" },
          { code: "MC20", desc: "Supercar (MC = Maserati Corse, 20 = any 2020)" },
        ],
        suffixes: [
          { code: "Trofeo", meaning: "Versió de màxima potència (V8 o V6 biturbo)" },
          { code: "GranSport", meaning: "Versió esportiva" },
          { code: "Folgore", meaning: "Versió 100% elèctrica (Folgore = llamp en italià)" },
        ],
      },
      {
        id: "fiat",
        name: "Fiat",
        logo: "F",
        logoSrc: "/logos/fiat-logo.png",
        color: "#B4020A",
        description: "Noms propis italians i números icònics. El Fiat 500 és un dels models més reconeguts del món des del 1957.",
        formula: "Número icònic / Nom propi",
        modelLines: [
          { code: "500", desc: "City car icònic (des de 1957)" },
          { code: "500e", desc: "Fiat 500 elèctric" },
          { code: "Panda", desc: "City car utilitari asequible" },
          { code: "Tipo", desc: "Compacte" },
          { code: "Doblo", desc: "Furgoneta familiar" },
          { code: "600e", desc: "SUV compacte elèctric nou" },
        ],
        note: "El número 500 fa referència al Fiat Nuova 500 de 1957 i la seva cilindrada de ~479cc. El 500 modern de 2007 és un homenatge amb el mateix esperit. El 500e porta endavant la tradició en versió elèctrica.",
      },
      {
        id: "lancia",
        name: "Lancia",
        logo: "L",
        logoSrc: "/logos/lancia-logo.png",
        color: "#002F6C",
        description: "Marca de luxe italiana del Grup Stellantis. Un passat gloriós en WRC. Ara en plena resurrecció amb un sol model.",
        formula: "Nom propi",
        modelLines: [
          { code: "Ypsilon", desc: "Compacte petit — l'únic model actual" },
          { code: "Delta (futur)", desc: "Anunciada pel 2028, revival del llegendari Delta Integrale" },
        ],
        note: "El Lancia Delta HF Integrale és considerat un dels millors cotxes de rally de tots els temps (6 campionats WRC seguits). Ara Lancia reneix gradualment, primer amb l'Ypsilon modernitzat.",
      },
      {
        id: "abarth",
        name: "Abarth",
        logo: "A",
        logoSrc: "/logos/abarth-logo.png",
        color: "#E8231E",
        description: "Marca esportiva italiana que afina cotxes Fiat. L'escorpió és el seu símbol. El número fa referència al Fiat base i a la tradició.",
        formula: "Número del Fiat base + tradició",
        modelLines: [
          { code: "Abarth 500e", desc: "Fiat 500e esportiu elèctric (261 CV)" },
          { code: "Abarth 600e", desc: "SUV esportiu basat en Fiat 600e" },
          { code: "Abarth 595", desc: "Clàssic llegendari (598cc original)" },
          { code: "Abarth 695", desc: "Versió extrema del 500 (695cc original)" },
        ],
        note: "Abarth 595 i 695 fan referència a la cilindrada dels motors originals dels anys 60. Carlo Abarth era austríac i va fundar Abarth a Torí el 1949. L'escorpí ve del seu signe zodiacal.",
      },
    ],
  },
  {
    label: "Japoneses",
    brands: [
      {
        id: "toyota",
        name: "Toyota",
        logo: "T",
        logoSrc: "/logos/toyota-logo.png",
        color: "#CC0000",
        description: "Noms propis en anglès. Pionera en híbrids amb el Prius (1997). GR és la divisió de competició. La marca de cotxes més venuda del món.",
        formula: "Nom propi / bZ + Número (elèctrics) / GR + Model (esportius)",
        modelLines: [
          { code: "Aygo X", desc: "Micro-urbà" },
          { code: "Yaris", desc: "Compacte petit" },
          { code: "Yaris Cross", desc: "Crossover petit" },
          { code: "Corolla", desc: "Compacte (el cotxe més venut de la història)" },
          { code: "Camry", desc: "Berlina mitjana" },
          { code: "C-HR", desc: "SUV coupé compacte" },
          { code: "RAV4", desc: "SUV compacte icònic (va inventar el segment el 1994)" },
          { code: "Land Cruiser", desc: "Tot-terreny llegendari" },
          { code: "Hilux", desc: "Pick-up llegendari" },
          { code: "GR86", desc: "Esportiu pur (2+2 places, motor atmosfèric)" },
          { code: "GR Yaris", desc: "Hatchback de competició WRC" },
          { code: "bZ4X", desc: "SUV elèctric" },
          { code: "Supra", desc: "Esportiu llegendari resurreccionat" },
        ],
        suffixes: [
          { code: "GR", meaning: "Gazoo Racing — versió de competició pura" },
          { code: "TRD", meaning: "Toyota Racing Development — pack d'accesoris esportius" },
          { code: "bZ", meaning: "Beyond Zero — gamma 100% elèctrica" },
          { code: "Hybrid / HEV", meaning: "Híbrid (tecnologia pròpia Toyota)" },
          { code: "PHEV", meaning: "Plug-in hybrid" },
        ],
        note: "El Prius va ser el primer híbrid de producció en sèrie del món (1997). La GR Yaris és un dels pocs cotxes del mercat dissenyat des de zero per a competir al WRC.",
      },
      {
        id: "honda",
        name: "Honda",
        logo: "H",
        logoSrc: "/logos/honda-logo.png",
        color: "#CC0000",
        description: "Noms propis i codis alfa-numèrics. VTEC és la seva tecnologia llegendària. La nova nomenclatura elèctrica usa e:Ny + número.",
        formula: "Nom propi / e:Ny + Número (elèctrics)",
        modelLines: [
          { code: "Jazz", desc: "Monovolum compacte" },
          { code: "Civic", desc: "Compacte llegendari" },
          { code: "HR-V", desc: "Crossover compacte" },
          { code: "ZR-V", desc: "SUV compacte" },
          { code: "CR-V", desc: "SUV mida mitjana (un dels primers SUVs del mercat, 1995)" },
          { code: "e:Ny1", desc: "SUV elèctric compacte" },
        ],
        suffixes: [
          { code: "i:MHEV", meaning: "Mild Hybrid Electric Vehicle (hibridació suau)" },
          { code: "i:HEV", meaning: "Full Hybrid (motor elèctric + tèrmic de Honda)" },
          { code: "VTEC", meaning: "Variable Valve Timing and Lift — tecnologia de distribució variable llegendària" },
          { code: "Type R", meaning: "Versió de competició extrema (Civic Type R, 330 CV)" },
          { code: "e:", meaning: "Gamma elèctrica" },
        ],
        note: "VTEC (Variable Valve Timing and Lift Electronic Control) és la tecnologia que fa que els motors Honda siguin tan revsadors. El Civic Type R és un dels hatchbacks de competició més valorats.",
      },
      {
        id: "nissan",
        name: "Nissan",
        logo: "N",
        logoSrc: "/logos/nissan-logo.png",
        color: "#C3002F",
        description: "Noms propis. El Qashqai va inventar el segment dels crossovers urbans el 2006. L'aliança amb Renault i Mitsubishi forma el grup més gran del món.",
        formula: "Nom propi",
        modelLines: [
          { code: "Micra", desc: "Compacte petit (discontinuat 2022)" },
          { code: "Juke", desc: "Crossover compacte amb disseny polaritzador" },
          { code: "Qashqai", desc: "Crossover compacte — va crear el segment el 2006" },
          { code: "X-Trail", desc: "SUV gran" },
          { code: "Ariya", desc: "SUV elèctric" },
          { code: "Leaf", desc: "Elèctric compacte pioner (2010)" },
          { code: "GT-R", desc: "Supercar llegendari (Godzilla)" },
          { code: "Navara", desc: "Pick-up" },
        ],
        suffixes: [
          { code: "e-Power", meaning: "Sistema únic: motor elèctric mou les rodes, tèrmic actua de generador (no PHEV)" },
          { code: "NISMO", meaning: "Nissan Motorsport — versió de competició extrema" },
        ],
        note: "El sistema e-Power de Nissan és únic: el motor de combustió mai no mou les rodes directament, sempre actua com a generador per al motor elèctric. El Leaf (2010) va ser el primer cotxe elèctric de gran producció.",
      },
      {
        id: "mazda",
        name: "Mazda",
        logo: "M",
        logoSrc: "/logos/mazda-logo.png",
        color: "#912636",
        description: "CX per als SUVs (Crossover), MX per als esportius, números per a berlinas. L'única marca que segueix amb el motor Wankel rotatiu.",
        formula: "CX + Número (SUVs) / MX + Número (esportius) / Mazda + Número (berlinas)",
        modelLines: [
          { code: "Mazda2", desc: "Compacte petit" },
          { code: "Mazda3", desc: "Compacte" },
          { code: "CX-30", desc: "SUV compacte" },
          { code: "CX-5", desc: "SUV mida mitjana" },
          { code: "CX-60", desc: "SUV gran PHEV" },
          { code: "CX-80", desc: "SUV gran 7 places" },
          { code: "CX-90", desc: "SUV de luxe gran" },
          { code: "MX-5", desc: "Roadster esportiu 2 places — el més venut de la història" },
          { code: "MX-30 R-EV", desc: "Crossover elèctric amb motor Wankel de rang" },
        ],
        note: "CX = Crossover. El número gran (60, 80, 90) = SUV més gran. MX-5 (Miata) és el roadster de dos places més venut de la història (un milió d'unitats). L'MX-30 és l'únic cotxe de producció amb motor Wankel rotatiu (com a generador).",
      },
      {
        id: "mitsubishi",
        name: "Mitsubishi",
        logo: "M",
        logoSrc: "/logos/mitsubishi-logo.png",
        color: "#CC0000",
        description: "Noms propis. Famosa pel Lancer Evolution en WRC. Ara part de l'aliança Renault-Nissan, amb models compartits.",
        formula: "Nom propi",
        modelLines: [
          { code: "Colt", desc: "Compacte (nou, basat en Renault Clio)" },
          { code: "ASX", desc: "SUV compacte (rellançat com Renault Austral adaptat)" },
          { code: "Eclipse Cross", desc: "SUV compacte coupé" },
          { code: "Outlander", desc: "SUV gran PHEV" },
          { code: "L200", desc: "Pick-up" },
        ],
        suffixes: [
          { code: "PHEV", meaning: "Plug-in Hybrid (Mitsubishi va ser pioner amb l'Outlander PHEV 2013)" },
          { code: "4WD", meaning: "Tracció total" },
        ],
        note: "El Lancer Evolution (Evo) és llegendari al WRC: múltiples títols mundials. El Mitsubishi Outlander PHEV (2013) va ser un dels primers SUVs PHEV del món. Ara Mitsubishi comparteix plataformes amb Renault i Nissan.",
      },
      {
        id: "subaru",
        name: "Subaru",
        logo: "S",
        logoSrc: "/logos/subaru-logo.png",
        color: "#003594",
        description: "Noms propis en anglès/japonès. Famosa per la tracció integral simètrica AWD i el motor Boxer. Dominant al WRC.",
        formula: "Nom propi",
        modelLines: [
          { code: "Impreza", desc: "Compacte" },
          { code: "XV / Crosstrek", desc: "Crossover compacte" },
          { code: "Forester", desc: "SUV compacte" },
          { code: "Outback", desc: "Familiar/SUV híbrid" },
          { code: "Legacy", desc: "Berlina/familiar" },
          { code: "BRZ", desc: "Esportiu 2+2 places (en col·laboració amb Toyota GR86)" },
          { code: "Solterra", desc: "SUV elèctric (col·laboració amb Toyota bZ4X)" },
        ],
        suffixes: [
          { code: "AWD", meaning: "All-Wheel Drive simètric (característic de Subaru, activa en tots els models)" },
          { code: "WRX", meaning: "World Rally eXperiment — berlina de competició" },
          { code: "STI", meaning: "Subaru Tecnica International — versió extrema de competició (Impreza WRX STI)" },
          { code: "XT", meaning: "Versió turbo de potència augmentada" },
        ],
        note: "El motor Boxer (cilindres horitzontals oposats) baixa el centre de gravetat del cotxe. L'AWD simètric és exclusiu de Subaru. L'Impreza WRX STI és llegendari al WRC (3 campionats mundials de constructors).",
      },
      {
        id: "suzuki",
        name: "Suzuki",
        logo: "S",
        logoSrc: "/logos/suzuki-logo.png",
        color: "#003087",
        description: "Noms propis compactes i memorials. Famosa per la lleugeresa i el Jimny off-road de culte.",
        formula: "Nom propi",
        modelLines: [
          { code: "Swift", desc: "Compacte petit icònic" },
          { code: "Ignis", desc: "Micro-SUV urbà" },
          { code: "Vitara", desc: "SUV compacte" },
          { code: "S-Cross", desc: "SUV compacte superior" },
          { code: "Jimny", desc: "Micro off-road llegendari (de culte mundial)" },
          { code: "Swace", desc: "Familiar hybrid (basat en Toyota Corolla Touring Sports)" },
          { code: "Across", desc: "SUV PHEV (basat en Toyota RAV4 PHEV)" },
        ],
        note: "Suzuki i Toyota van firmar un acord de col·laboració el 2019 per compartir tecnologia híbrida. El Jimny és cult: el tot-terreny petit per a qui realment surt de l'asfalt. Lleugera i pur tot-terreny, sense confort.",
      },
      {
        id: "lexus",
        name: "Lexus",
        logo: "L",
        logoSrc: "/logos/lexus-logo.png",
        color: "#1A1A1A",
        description: "Marca premium de Toyota. Lletra (segment) + número (mida creixent). Sistema paral·lel al BMW però amb filosofia japonesa Omotenashi (hospitalitat).",
        formula: "Lletra + Número (mida creixent)",
        modelLines: [
          { code: "LBX", desc: "SUV petit" },
          { code: "UX", desc: "Crossover compacte" },
          { code: "NX", desc: "SUV compacte" },
          { code: "RX", desc: "SUV gran" },
          { code: "GX", desc: "SUV off-road gran" },
          { code: "LX", desc: "SUV de luxe màxim" },
          { code: "IS", desc: "Berlina compacte" },
          { code: "ES", desc: "Berlina gran" },
          { code: "LS", desc: "Berlina de luxe suprem" },
          { code: "LC", desc: "Coupé gran de luxe" },
          { code: "RZ", desc: "SUV elèctric" },
        ],
        suffixes: [
          { code: "h", meaning: "Hybrid — motor tèrmic + elèctric" },
          { code: "F", meaning: "Versió de competició extrema (RC F, IS F — equivalent al M de BMW)" },
          { code: "F Sport", meaning: "Pack esportiu estètic (NO és la versió F, com el M Sport ≠ M de BMW)" },
        ],
        note: "F Sport ≠ versió F. La versió F (RC F, IS F) és el cotxe de competició. F Sport és un pack estètic. La lletra U/N/R/G/L indica mida creixent als SUVs. I/E/L indica mida creixent a les berlinas.",
      },
    ],
  },
  {
    label: "Coreanes",
    brands: [
      {
        id: "hyundai",
        name: "Hyundai",
        logo: "H",
        logoSrc: "/logos/hyundai-logo.png",
        color: "#002C5F",
        description: "Noms propis i sistema 'i + número' per a berlinas. IONIQ és la gamma elèctrica nativa. La marca coreana que ha superat totes les expectatives de qualitat.",
        formula: "Nom propi / i + Número (berlinas) / IONIQ + Número (elèctrics)",
        modelLines: [
          { code: "i10", desc: "Micro-urbà" },
          { code: "i20", desc: "Compacte petit" },
          { code: "i30", desc: "Compacte" },
          { code: "Kona", desc: "Crossover compacte" },
          { code: "Tucson", desc: "SUV compacte" },
          { code: "Santa Fe", desc: "SUV gran" },
          { code: "IONIQ 5", desc: "SUV elèctric compacte" },
          { code: "IONIQ 6", desc: "Berlina elèctrica fastback" },
          { code: "IONIQ 9", desc: "SUV gran elèctric 7 places" },
          { code: "NEXO", desc: "Pila de combustible d'hidrogen" },
        ],
        suffixes: [
          { code: "N", meaning: "Versió de competició (N Division, inspirada en Namyang i Nürburgring)" },
          { code: "N Line", meaning: "Pack estètic esportiu (NO és versió N)" },
          { code: "Electric", meaning: "Versió 100% elèctrica" },
        ],
        note: "N Line ≠ versió N. La versió N (i20 N, i30 N, Kona N) és un cotxe de competició real. N Line és un pack estètic. El nom N fa referència a Namyang (centre R&D de Hyundai) i al Nürburgring.",
      },
      {
        id: "kia",
        name: "KIA",
        logo: "K",
        logoSrc: "/logos/kia-logo.png",
        color: "#BB162B",
        description: "Noms propis per a combustió, EV + número per a la gamma elèctrica nativa. KIA = 'Arise from Asia' en koreà.",
        formula: "Nom propi / EV + Número (elèctrics)",
        modelLines: [
          { code: "Picanto", desc: "Micro-urbà" },
          { code: "Stonic", desc: "Crossover petit" },
          { code: "Niro", desc: "Crossover ecològic (hybrid/PHEV/elèctric)" },
          { code: "Sportage", desc: "SUV compacte" },
          { code: "Sorento", desc: "SUV gran" },
          { code: "EV3", desc: "SUV elèctric petit" },
          { code: "EV6", desc: "Fastback/SUV elèctric — molt valorat" },
          { code: "EV9", desc: "SUV gran elèctric 7 places" },
        ],
        note: "EV + número segueix la lògica de mida: EV3 petit, EV6 mig, EV9 gran. El Stinger GT (berlina esportiva de 366 CV) va trencar tots els prejudicis sobre les marques coreanes. El EV6 va guanyar l'European Car of the Year 2022.",
      },
      {
        id: "kgm",
        name: "KGM",
        logo: "K",
        logoSrc: "/logos/kgm-logo.png",
        color: "#1B3A6B",
        description: "Antiga SsangYong, ara KGM (KG Mobility) des del 2023. Models robustos coreans orientats al tot-terreny i a les famílies.",
        formula: "Nom propi",
        modelLines: [
          { code: "Tivoli", desc: "SUV compacte" },
          { code: "Torres", desc: "SUV compacte (nom de Torres del Paine, Xile)" },
          { code: "Torres EVX", desc: "Torres 100% elèctric" },
          { code: "Rexton", desc: "SUV gran tot-terreny" },
          { code: "Musso", desc: "Pick-up" },
        ],
        note: "SsangYong va fer fallida el 2022 i va ser adquirida per KG Group el 2023. El nou nom és KGM (KG Mobility). Torres fa referència a Torres del Paine (Patagònia xilena), evocant robustesa i aventura.",
      },
    ],
  },
  {
    label: "Britàniques",
    brands: [
      {
        id: "land-rover",
        name: "Land Rover",
        logo: "L",
        logoSrc: "/logos/land-rover-logo.png",
        color: "#1A5276",
        description: "Noms propis icònics amb molt de caràcter. El Range Rover va inventar el SUV de luxe el 1970. Ara part del grup Tata (Índia).",
        formula: "Nom propi (Range Rover, Defender, Discovery...)",
        modelLines: [
          { code: "Defender", desc: "Tot-terreny icònic (ressuscitat 2020, llegendari des del 1948)" },
          { code: "Discovery", desc: "SUV familiar gran" },
          { code: "Discovery Sport", desc: "SUV familiar compacte" },
          { code: "Range Rover Evoque", desc: "SUV de luxe compacte" },
          { code: "Range Rover Velar", desc: "SUV de disseny premium" },
          { code: "Range Rover Sport", desc: "SUV de luxe esportiu" },
          { code: "Range Rover", desc: "SUV de luxe suprem" },
        ],
        note: "El Range Rover (1970) va inventar el segment dels SUVs de luxe. El Defender original (1948-2016) va ser el vehicle de l'exèrcit britànic i d'exploradors de tot el món. El nou Defender (2020) ho modernitza tot.",
      },
      {
        id: "jaguar",
        name: "Jaguar",
        logo: "J",
        logoSrc: "/logos/jaguar-logo.png",
        color: "#1E3A5F",
        description: "Noms amb lletres: X per a berlinas, F per a esportius/SUVs, I per a elèctrics. En plena transformació elèctrica total (2025+).",
        formula: "Lletra + Sufix (Type / Pace)",
        modelLines: [
          { code: "XE", desc: "Berlina compacte" },
          { code: "XF", desc: "Berlina gran" },
          { code: "F-Type", desc: "Esportiu coupé/cabrio icònic" },
          { code: "E-Pace", desc: "SUV compacte" },
          { code: "F-Pace", desc: "SUV esportiu" },
          { code: "I-Pace", desc: "SUV elèctric (pioner de Jaguar en EVs)" },
          { code: "Nou Jaguar (2025)", desc: "Sedà de luxe elèctric pur, nova identitat" },
        ],
        note: "Jaguar va anunciar el 2024 una transformació radical: marca de luxe elèctric pur des del 2025. El rebranding va ser polèmic però la nova direcció apunta a Bentley i Rolls-Royce elèctrics.",
      },
      {
        id: "bentley",
        name: "Bentley",
        logo: "B",
        logoSrc: "/logos/bentley-logo.png",
        color: "#1C6B40",
        description: "Noms propis amb llarga tradició. Mega-luxe britànic part del Grup Volkswagen. W.O. Bentley va fundar la marca el 1919.",
        formula: "Nom propi",
        modelLines: [
          { code: "Continental GT", desc: "Coupé de gran turisme" },
          { code: "Continental GTC", desc: "Cabrio de gran turisme" },
          { code: "Flying Spur", desc: "Berlina ultra-luxe" },
          { code: "Bentayga", desc: "SUV de luxe suprem" },
        ],
        suffixes: [
          { code: "W12", meaning: "Motor 12 cilindres en W — màxima potència" },
          { code: "V8", meaning: "Motor V8 biturbo (~550 CV)" },
          { code: "Speed", meaning: "Versió de màxima potència" },
          { code: "Mulliner", meaning: "Departament artesanal — personalització extrema" },
          { code: "S", meaning: "Versió esportiva" },
        ],
      },
      {
        id: "aston-martin",
        name: "Aston Martin",
        logo: "A",
        logoSrc: "/logos/aston-martin-logo.png",
        color: "#006A4E",
        description: "Noms propis i la sèrie DB (David Brown). DB = David Brown, l'empresari que va salvar la marca el 1947. Icona del cinema per James Bond.",
        formula: "DB + Número / Nom propi",
        modelLines: [
          { code: "Vantage", desc: "Esportiu base (~665 CV V8)" },
          { code: "DB12", desc: "GT coupé (successor del DB11)" },
          { code: "DBS", desc: "GT de màxima potència" },
          { code: "DBX", desc: "SUV (primer de la marca)" },
          { code: "Valkyrie", desc: "Hypercar de col·lecció (1.160 CV V12)" },
        ],
        note: "DB ve de David Brown, que va comprar Aston Martin el 1947. Des del DB2 fins el DB12, tota una saga. James Bond condueix Aston Martins des del 1964 (Goldfinger amb l'Aston Martin DB5, el cotxe més famós de la història del cinema).",
      },
      {
        id: "rolls-royce",
        name: "Rolls-Royce",
        logo: "R",
        logoSrc: "/logos/rolls-royce-logo.png",
        color: "#3D0C45",
        description: "El luxe absolut. Noms evocadors de poder i misteri. Tots els models actuals estan electrificant-se. Part del Grup BMW.",
        formula: "Nom propi evocador",
        modelLines: [
          { code: "Phantom", desc: "Berlina de luxe suprem (el millor cotxe del món)" },
          { code: "Ghost", desc: "Berlina més 'accessible' de la gamma" },
          { code: "Cullinan", desc: "SUV de luxe suprem (nom del diamant més gran del món)" },
          { code: "Spectre", desc: "Coupé elèctric (primer 100% elèctric de Rolls-Royce)" },
          { code: "Wraith", desc: "Coupé (discontinuat)" },
          { code: "Dawn", desc: "Cabrio (discontinuat)" },
        ],
        note: "Tots els noms evoquen misteri i poder: Phantom (fantasma), Ghost (esperit), Wraith (aparegut), Cullinan (el diamant més gran del món, 3.106 quirats). El Spectre és el primer elèctric de la història de Rolls-Royce (2023).",
      },
      {
        id: "lotus",
        name: "Lotus",
        logo: "L",
        logoSrc: "/logos/lotus-logo.png",
        color: "#006B3C",
        description: "Llegenda britànica de la lleugeresa i el comportament pur. Molts models comencen amb 'E'. Ara propietat de Geely (Xina), en transició cap a EVs.",
        formula: "Nom propi (molts comencen per E)",
        modelLines: [
          { code: "Emira", desc: "Esportiu de combustió — l'últim Lotus de gasolina" },
          { code: "Eletre", desc: "SUV elèctric de luxe (nou)" },
          { code: "Evija", desc: "Hypercar elèctric (2.000 CV)" },
          { code: "Emeya", desc: "Berlina esportiva elèctrica" },
        ],
        note: "Colin Chapman, fundador de Lotus, tenia el mantra: 'Simplify, then add lightness'. L'Emira és l'últim Lotus de combustió. Des del 2022, Lotus fabrica SUVs de luxe elèctrics. L'Evija és el cotxe elèctric de producció en sèrie més potent (~2.000 CV).",
      },
      {
        id: "caterham",
        name: "Caterham",
        logo: "C",
        logoSrc: "/logos/caterham-logo.png",
        color: "#CC0000",
        description: "Fabrica exclusivament variants del Seven des del 1973. El número indica aproximadament la potència. Pur plaer de conduir, sense concessió.",
        formula: "Seven + Número (potència aproximada)",
        modelLines: [
          { code: "Seven 170", desc: "El més lleuger — 440 kg, motor monocilindre Suzuki" },
          { code: "Seven 270", desc: "El punt d'entrada — esportiu per a carretera" },
          { code: "Seven 360", desc: "Molt esportiu" },
          { code: "Seven 420", desc: "Per a pista" },
          { code: "Seven 485", desc: "Extrem" },
          { code: "Seven 620R", desc: "600+ CV en 550 kg — 0-100 en 2,5 s" },
        ],
        note: "El Caterham Seven és bàsicament el Lotus Seven original de 1957 que Colin Chapman va dissenyar. Sense sostre, sense airbags, sense assistències. El 170 pesa menys de 440 kg: menys que moltes motos. El 620R és, en relació pes/potència, un dels cotxes més extrems que es pot comprar.",
      },
    ],
  },
  {
    label: "Americanes",
    brands: [
      {
        id: "ford",
        name: "Ford",
        logo: "F",
        logoSrc: "/logos/ford-logo.png",
        color: "#003476",
        description: "Noms propis en anglès amb arrels en la tradició americana. El Mustang és la llegenda. Henry Ford va revolucionar la indústria amb la producció en cadena el 1913.",
        formula: "Nom propi",
        modelLines: [
          { code: "Puma", desc: "SUV compacte europeu" },
          { code: "Kuga", desc: "SUV mida mitjana" },
          { code: "Explorer", desc: "SUV gran (nou elèctric a Europa)" },
          { code: "Mustang", desc: "Esportiu llegendari (des del 1964)" },
          { code: "Mustang Mach-E", desc: "SUV elèctric" },
          { code: "Ranger", desc: "Pick-up" },
          { code: "Bronco", desc: "Tot-terreny ressuscitat (inspirat en l'original de 1966)" },
          { code: "F-150", desc: "Pick-up gran — el vehicle més venut als EUA des de fa 40 anys" },
        ],
        suffixes: [
          { code: "ST", meaning: "Sport Technologies — versió esportiva intermèdia" },
          { code: "RS", meaning: "Racing Sport — versió extrema de circuit (Focus RS, Fiesta RS)" },
          { code: "GT", meaning: "Gran Turisme (Mustang GT)" },
          { code: "Shelby GT500", meaning: "Versió extrema del Mustang (760 CV)" },
          { code: "Raptor", meaning: "Versió off-road extrema (Ranger Raptor, F-150 Raptor)" },
        ],
        note: "El Ford F-150 és el vehicle (no cotxe) més venut als EUA durant 40 anys consecutius. El Mustang és el muscle car americà per excel·lència (des del 1964). El Bronco Original va ser el cotxe de la icònica persecució de O.J. Simpson el 1994.",
      },
      {
        id: "jeep",
        name: "Jeep",
        logo: "J",
        logoSrc: "/logos/jeep-logo.png",
        color: "#4A6741",
        description: "Noms propis americans. Tradició off-road des del 1941 (Segon. Guerra Mundial). El Wrangler és la icona absoluta.",
        formula: "Nom propi (referència americana / natura)",
        modelLines: [
          { code: "Avenger", desc: "SUV compacte urbà — primer Jeep elèctric" },
          { code: "Renegade", desc: "SUV compacte" },
          { code: "Compass", desc: "SUV compacte-mig" },
          { code: "Wrangler", desc: "Tot-terreny icònic (evolució directa del Jeep original de 1941)" },
          { code: "Grand Cherokee", desc: "SUV gran" },
          { code: "Gladiator", desc: "Pick-up basat en Wrangler" },
        ],
        note: "El Jeep original va néixer el 1941 per a ús militar. El nom JEEP ve de GP (General Purpose = ús general). El Rubicon és la variant off-road extrema de molts models Jeep, nom del cèlebre riu que Cèsar va creuar.",
      },
      {
        id: "dodge",
        name: "Dodge",
        logo: "D",
        logoSrc: "/logos/dodge-logo.png",
        color: "#D62B1F",
        description: "Muscle cars americans amb noms d'armes i força. El Charger i Challenger han estat icones culturals americanes durant 50 anys.",
        formula: "Nom propi (referència a poder / armes)",
        modelLines: [
          { code: "Hornet", desc: "SUV compacte (nou)" },
          { code: "Durango", desc: "SUV gran" },
          { code: "Charger", desc: "Berlina muscle (ara elèctrica)" },
          { code: "Challenger", desc: "Muscle coupé (descontinuat 2023)" },
        ],
        note: "El Dodge Charger reneix el 2024 com a elèctric (Dodge Charger Daytona EV). El nou Charger EV inclou un sistema de so artificial dels escapes per mantenir l'experiència sonora — l'únic EV del món dissenyat per sonar com un V8.",
      },
      {
        id: "ram",
        name: "RAM",
        logo: "R",
        logoSrc: "/logos/ram-logo.png",
        color: "#C8102E",
        description: "Marca exclusiva de pick-ups i vehicles comercials. El nom ve del moltó (mascle del xai), evocant força. Es va separar de Dodge el 2010.",
        formula: "Número (classe de càrrega) / Nom propi",
        modelLines: [
          { code: "RAM 1500", desc: "Pick-up mida completa" },
          { code: "RAM 1500 REV", desc: "Pick-up 100% elèctric" },
          { code: "RAM 2500 / 3500", desc: "Pick-ups pesades per a treball dur" },
          { code: "RAM ProMaster", desc: "Furgoneta comercial" },
        ],
        note: "El número (1500/2500/3500) indica originalment el pes de càrrega màxim en centenars de lliures. RAM es va separar de Dodge el 2010 com a marca independent per a vehicles de treball.",
      },
      {
        id: "tesla",
        name: "Tesla",
        logo: "T",
        logoSrc: "/logos/tesla-logo.png",
        color: "#CC0000",
        description: "Lletres seguint l'abecedari. Tesla va intentar que S-3-X-Y formessin la paraula 'SEXY'. Tots els models 100% elèctrics.",
        formula: "Model + Lletra",
        modelLines: [
          { code: "Model 3", desc: "Berlina elèctrica compacte — la més venuda" },
          { code: "Model S", desc: "Berlina elèctrica gran (S de Sedan)" },
          { code: "Model X", desc: "SUV elèctric gran (portes d'ala de falcó)" },
          { code: "Model Y", desc: "SUV elèctric compacte — el cotxe més venut del món el 2023" },
          { code: "Cybertruck", desc: "Pick-up elèctric futurista" },
        ],
        suffixes: [
          { code: "Standard Range (SR)", meaning: "Autonomia estàndard (~400 km)" },
          { code: "Long Range (LR)", meaning: "Autonomia ampliada (~600 km)" },
          { code: "Performance (P)", meaning: "Versió de màxima potència" },
          { code: "Plaid", meaning: "Versió extrema (1.020 CV al Model S Plaid, 0-100 en 2.1s)" },
        ],
        note: "Tesla va intentar que les lletres formessin 'SEXY': Model S, E (Ford tenia la marca per a l'E, van usar el 3), X, Y. El Model Y és el cotxe més venut del món el 2023 (1.2M unitats). Plaid és una referència a la pel·lícula Spaceballs ('Ludicrous Speed').",
      },
    ],
  },
  {
    label: "Espanyoles",
    brands: [
      {
        id: "seat",
        name: "SEAT",
        logo: "S",
        logoSrc: "/logos/seat-logo.png",
        color: "#E2001A",
        description: "Filial de Volkswagen des del 1986. Noms de llocs hispànics: Ibiza, Arona, Ateca, Tarraco, León. SEAT = Sociedad Española de Automóviles de Turismo.",
        formula: "Nom de lloc hispànic",
        modelLines: [
          { code: "Ibiza", desc: "Compacte petit (nom de l'illa balear)" },
          { code: "Leon", desc: "Compacte (nom de la ciutat castellana)" },
          { code: "Arona", desc: "SUV petit (localitat de Tenerife)" },
          { code: "Ateca", desc: "SUV compacte (municipi d'Aragó)" },
          { code: "Tarraco", desc: "SUV gran (nom romà de Tarragona)" },
        ],
        suffixes: [
          { code: "FR", meaning: "Fun Racing — versió esportiva" },
          { code: "Xcellence", meaning: "Equipament alt premium" },
          { code: "TGI", meaning: "Gas natural comprimit" },
          { code: "TSI / TDI", meaning: "Gasolina / Dièsel (motors compartits amb VW)" },
        ],
        note: "Quasi tots els models SEAT porten noms de llocs de la Península Ibèrica o les Illes Balears i Canàries. CUPRA va ser inicialment un sufix de SEAT (Leon Cupra) abans de convertir-se en marca independent el 2018.",
      },
      {
        id: "cupra",
        name: "CUPRA",
        logo: "C",
        logoSrc: "/logos/cupra-logo.png",
        color: "#C2973A",
        description: "Sub-marca esportiva de SEAT, independent des del 2018. Noms propis amb caràcter mediterrani. CUPRA = Cup Racing.",
        formula: "Nom propi (lloc / concepte mediterrani)",
        modelLines: [
          { code: "Formentor", desc: "SUV coupé (Punta de Formentor, Menorca)" },
          { code: "Leon", desc: "Berlina/familiar esportiva" },
          { code: "Ateca", desc: "SUV compacte esportiu" },
          { code: "Born", desc: "Compacte elèctric (El Born, barri de Barcelona)" },
          { code: "Terramar", desc: "SUV coupé nou (Circuit de Terramar, Sitges)" },
          { code: "Tavascan", desc: "SUV coupé elèctric (vall pirinenca de l'Alt Àneu)" },
        ],
        note: "Tots els models CUPRA porten noms de llocs i conceptes mediterranis o catalans: El Born (barri barceloní), Terramar (circuit de motorsport de Sitges del 1923), Tavascan (vall dels Pirineus catalans), Formentor (la punta de Menorca).",
      },
      {
        id: "ebro",
        name: "EBRO",
        logo: "E",
        logoSrc: "/logos/ebro-logo.png",
        color: "#003087",
        description: "Marca espanyola ressuscitada el 2024 per a camionetes elèctriques. El nom fa referència al riu Ebre. Original: va operar de 1954 a 1986 amb llicència Nissan.",
        formula: "Codi alfanumèric",
        modelLines: [
          { code: "S700", desc: "Camioneta pick-up elèctrica — fabricada a Barcelona" },
        ],
        note: "EBRO va ser una marca espanyola d'origen japonès (llicència Nissan) activa de 1954 a 1986. Ressuscitada el 2024 com a marca de vehicles comercials elèctrics, amb la S700 assemblada a la Zona Franca de Barcelona.",
      },
      {
        id: "evo",
        name: "EVO",
        logo: "E",
        logoSrc: "/logos/evo-logo.png",
        color: "#00A19A",
        description: "Marca espanyola nova de vehicles elèctrics del grup SILENCE. Orientada a cotxes urbans compactes i flotes.",
        formula: "Nom propi",
        modelLines: [
          { code: "EVO Electric", desc: "Compacte elèctric urbà" },
        ],
        note: "EVO és del grup espanyol SILENCE, conegut per patinets i motos elèctrics. Apunta principalment a flotes urbanes i lloguer de cotxes elèctrics.",
      },
    ],
  },
  {
    label: "Escandinaves",
    brands: [
      {
        id: "volvo",
        name: "Volvo",
        logo: "V",
        logoSrc: "/logos/volvo-logo.png",
        color: "#003057",
        description: "Lletra (tipus de carrosseria) + número (mida creixent). Pionera en seguretat viària: va inventar el cinturó de 3 punts el 1959. Ara propietat de Geely (Xina).",
        formula: "Lletra + Número (mida: 40=petit, 60=mig, 90=gran)",
        modelLines: [
          { code: "XC40", desc: "SUV compacte (XC = Crossover, 40 = petit)" },
          { code: "XC60", desc: "SUV mig — el model més venut de Volvo" },
          { code: "XC90", desc: "SUV gran" },
          { code: "S60", desc: "Berlina compacte (S = Saloon)" },
          { code: "S90", desc: "Berlina gran" },
          { code: "V60", desc: "Familiar compacte (V = Versatile)" },
          { code: "V90", desc: "Familiar gran" },
          { code: "C40", desc: "SUV coupé elèctric" },
          { code: "EX30", desc: "SUV elèctric petit — el més assequible de Volvo" },
          { code: "EX40", desc: "XC40 elèctric renombrat (EX = Electric Crossover)" },
          { code: "EX90", desc: "Gran SUV elèctric 7 places" },
        ],
        note: "El número indica la mida: 40 = petit, 60 = mitjà, 90 = gran. La lletra indica el tipus: S = berlina, V = familiar, XC = SUV, EX = elèctric. Volvo va inventar el cinturó de seguretat de 3 punts el 1959 i va cedir la patent lliurement per salvar vides.",
      },
      {
        id: "polestar",
        name: "Polestar",
        logo: "P",
        logoSrc: "/logos/polestar-logo.png",
        color: "#0A0A0A",
        description: "Marca elèctrica joint venture Volvo/Geely. Números progressius per a cada model. Originalment la divisió de competició de Volvo.",
        formula: "Polestar + Número",
        modelLines: [
          { code: "Polestar 2", desc: "Berlina/fastback elèctrica" },
          { code: "Polestar 3", desc: "SUV gran elèctric" },
          { code: "Polestar 4", desc: "SUV coupé elèctric (únic: sense lluna posterior)" },
          { code: "Polestar 5", desc: "GT elèctric (futur)" },
          { code: "Polestar 6", desc: "Roadster elèctric (futur)" },
        ],
        note: "Polestar 4 és únic: no té lluna posterior, substituïda per una càmera de marxa enrere interior. Polestar 1 (PHEV, 600 CV, 1.500 unitats) va ser el primer model. Originalment Polestar era la divisió de competició de Volvo.",
      },
    ],
  },
  {
    label: "Xineses",
    brands: [
      {
        id: "byd",
        name: "BYD",
        logo: "B",
        logoSrc: "/logos/byd-logo.png",
        color: "#1F2B6C",
        description: "BYD = Build Your Dreams. El major fabricant d'elèctrics del món el 2023. Noms poètics: Ocean Series (animals) i Dynasty Series (dinasties xineses).",
        formula: "Nom propi (animal / dinastia xinesa)",
        modelLines: [
          { code: "Seagull", desc: "Micro-urbà elèctric (Ocean Series)" },
          { code: "Dolphin", desc: "Compacte elèctric (Ocean Series)" },
          { code: "Atto 3", desc: "SUV compacte elèctric (disponible a Europa)" },
          { code: "Seal", desc: "Berlina elèctrica (Ocean Series)" },
          { code: "Sealion 6", desc: "SUV PHEV" },
          { code: "Sealion 7", desc: "SUV elèctric" },
          { code: "Tang", desc: "SUV gran (Dinastia Tang)" },
          { code: "Han", desc: "Berlina gran (Dinastia Han)" },
        ],
        note: "BYD va superar Tesla en vendes d'elèctrics el 2023 a escala mundial. Ocean Series: animals marins (Seal, Dolphin, Seagull). Dynasty Series: grans dinasties xineses (Han, Tang, Song, Yuan). DM = Dual Mode (PHEV propi).",
      },
      {
        id: "mg",
        name: "MG",
        logo: "M",
        logoSrc: "/logos/mg-logo.png",
        color: "#C41230",
        description: "Marca britànica clàssica (Morris Garages, 1924), ara propietat de SAIC Motor (Xina). Sistema numèric per als cotxes (MG3, MG4…). L'MG4 és un dels elèctrics més valorats del segment.",
        formula: "MG + Número / ZS / HS",
        modelLines: [
          { code: "MG3", desc: "Compacte petit (mild hybrid)" },
          { code: "MG4", desc: "Elèctric compacte — Car of the Year en múltiples mercats" },
          { code: "MG5", desc: "Familiar elèctric" },
          { code: "ZS", desc: "SUV compacte (ZS = nom homenatge al clàssic MG ZS)" },
          { code: "HS", desc: "SUV gran" },
          { code: "Marvel R", desc: "SUV elèctric" },
        ],
        note: "MG va ser la marca britànica icònica d'esportius (MGB, MGA, Midget) des del 1924. Des del 2007 és propietat de SAIC Motor (Xina). L'MG4 va guanyar el 'Car of the Year' en molts mercats europeus el 2023.",
      },
      {
        id: "maxus",
        name: "Maxus",
        logo: "M",
        logoSrc: "/logos/maxus-logo.png",
        color: "#003087",
        description: "Filial de SAIC Motor. Furgonetes, SUVs i pick-ups. Orientada a vehicles comercials i famílies. Noms propis.",
        formula: "Nom propi / Codi",
        modelLines: [
          { code: "T60", desc: "Pick-up" },
          { code: "Deliver 9", desc: "Furgoneta comercial elèctrica" },
          { code: "Mifa 9", desc: "MPV de luxe elèctric" },
          { code: "Euniq 6", desc: "MPV elèctric de 6/7 places" },
        ],
      },
      {
        id: "dfsk",
        name: "DFSK",
        logo: "D",
        logoSrc: "/logos/dfsk-logo.png",
        color: "#003087",
        description: "DFSK = Dongfeng Sokon Motors. Marca xinesa coneguda a Europa per furgonetes comercials elèctriques assequibles.",
        formula: "Codi alfanumèric",
        modelLines: [
          { code: "EC35", desc: "Furgoneta elèctrica compacte" },
          { code: "EC75", desc: "Furgoneta elèctrica gran" },
          { code: "Glory 500", desc: "SUV" },
        ],
      },
      {
        id: "omoda",
        name: "Omoda",
        logo: "O",
        logoSrc: "/logos/omoda-logo.png",
        color: "#C8102E",
        description: "Sub-marca premium de Chery (Xina). C + número. Disseny orientat al públic jove europeu.",
        formula: "C + Número",
        modelLines: [
          { code: "Omoda C5", desc: "SUV compacte" },
          { code: "Omoda C9", desc: "SUV gran" },
        ],
        note: "Omoda i Jaecoo comparteixen el mateix grup (Chery) però apunten a segments diferents: Omoda és urbana i moderna, Jaecoo és off-road i aventurera.",
      },
      {
        id: "jaecoo",
        name: "Jaecoo",
        logo: "J",
        logoSrc: "/logos/jaecoo-logo.png",
        color: "#2C4A8E",
        description: "Sub-marca off-road de Chery (Xina). J + número. Disseny robust orientat a l'aventura.",
        formula: "J + Número",
        modelLines: [
          { code: "Jaecoo J7", desc: "SUV off-road mida mitjana" },
          { code: "Jaecoo J8", desc: "SUV off-road gran" },
        ],
      },
      {
        id: "lynk-co",
        name: "Lynk & Co",
        logo: "L",
        logoSrc: "/logos/lynk-co-logo.png",
        color: "#0A0A0A",
        description: "Joint venture Geely-Volvo. Números amb 0 davant. Subscripció com a model alternatiu a la propietat. Plataforma CMA compartida amb Volvo XC40.",
        formula: "0 + Número",
        modelLines: [
          { code: "01", desc: "SUV gran" },
          { code: "02", desc: "Crossover coupé" },
          { code: "05", desc: "SUV PHEV" },
          { code: "06", desc: "SUV petit elèctric" },
          { code: "08", desc: "SUV PHEV gran" },
        ],
        note: "Lynk & Co proposa subscripció: pagues una quota mensual i pots conduir el cotxe sense comprar-lo, o compartir-lo quan no l'uses per reduir costos. Plataforma CMA compartida amb Volvo XC40.",
      },
      {
        id: "livan",
        name: "Livan",
        logo: "L",
        logoSrc: "/logos/livan-logo.png",
        color: "#1B3A6B",
        description: "Filial de Geely per a vehicles familiars assequibles. Noms propis. Principalment al mercat xinès però amb expansió a Europa.",
        formula: "Nom propi",
        modelLines: [
          { code: "Livan 9", desc: "MPV elèctric familiar" },
        ],
      },
      {
        id: "xpeng",
        name: "Xpeng",
        logo: "X",
        logoSrc: "/logos/xpeng-logo.png",
        color: "#1E8AFE",
        description: "Marca xinesa d'elèctrics tecnològics. G per a SUVs, P per a berlinas. S'ha associat amb Volkswagen per a tecnologia elèctrica a la Xina.",
        formula: "G + Número (SUVs) / P + Número (berlinas)",
        modelLines: [
          { code: "G6", desc: "SUV compacte elèctric" },
          { code: "G9", desc: "SUV gran elèctric" },
          { code: "P7", desc: "Berlina esportiva elèctrica" },
          { code: "X9", desc: "MPV gran elèctric" },
        ],
        note: "Xpeng (XPEV en borsa) ve de He Xiaopeng, el fundador. VW ha invertit en Xpeng per accedir a la seva tecnologia elèctrica. Xpeng és reconeguda per les seves capacitats avançades d'assistència a la conducció.",
      },
    ],
  },
];

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5 text-muted-foreground">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5 text-amber-500">
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-3">{children}</p>
);

export const GuiaModels = () => {
  const [isDark, setIsDark] = useState(
    () => typeof window !== "undefined" && document.documentElement.classList.contains("dark")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const chipBg    = (c: string) => `${c}${isDark ? "30" : "18"}`;
  const chipBorder = (c: string) => `${c}${isDark ? "55" : "30"}`;
  const pillBg    = (c: string) => `${c}${isDark ? "28" : "15"}`;

  const allBrands = brandGroups.flatMap((g) => g.brands).sort((a, b) => a.name.localeCompare(b.name, "ca"));

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-4xl">
        {/* Page header */}
        <div className="mb-6 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-foreground">
            Guia: Models de cotxes
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base max-w-lg mx-auto leading-relaxed">
            Cada marca té la seva pròpia lògica. Aquí t'expliquem com llegir el nom d'un cotxe i saber exactament el que estàs mirant.
          </p>
        </div>

        {/* Test card - full width */}
        <Link
          to="/guia-models/test"
          className="group flex items-center gap-4 px-5 py-4 mb-4 bg-card border border-border rounded-2xl hover:border-foreground/30 hover:shadow-md transition-all duration-200 w-full"
        >
          <span className="text-2xl shrink-0">🧪</span>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-foreground text-sm">Test de coneixement</p>
            <p className="text-xs text-muted-foreground mt-0.5">Posa a prova el que has après</p>
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </Link>

        {/* Brand navigation grid */}
        <div className="mb-12 bg-card border border-border rounded-2xl p-5 sm:p-6">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-5">Navega per marca</p>
          <div className="flex flex-wrap gap-2">
            {allBrands.map((brand) => (
              <a
                key={brand.id}
                href={`#${brand.id}`}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border hover:border-border/80 hover:bg-secondary/60 transition-all group"
              >
                <div className="w-5 h-5 rounded overflow-hidden bg-white border border-black/10 dark:border-white/15 flex items-center justify-center shrink-0">
                  <img
                    src={brand.logoSrc}
                    alt={brand.name}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      const el = e.currentTarget;
                      el.style.display = "none";
                      el.parentElement!.style.backgroundColor = brand.color;
                      el.parentElement!.innerHTML = `<span style="color:white;font-weight:900;font-size:0.55rem">${brand.logo}</span>`;
                    }}
                  />
                </div>
                <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">{brand.name}</span>
              </a>
            ))}
          </div>
        </div>

        {/* Brand sections */}
        <div className="space-y-8">
          {allBrands.map((brand) => (
            <section id={brand.id} key={brand.id} className="bg-card border border-border rounded-2xl overflow-hidden scroll-mt-20">
                    {/* Colored top bar */}
                    <div className="h-1 w-full" style={{ backgroundColor: brand.color }} />

                    {/* Brand header */}
                    <div className="flex items-center gap-4 px-6 py-5 border-b border-border">
                      <div className="w-11 h-11 rounded-xl shrink-0 overflow-hidden bg-white border border-black/15 dark:border-white/20 flex items-center justify-center p-1.5">
                        <img
                          src={brand.logoSrc}
                          alt={brand.name}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            const el = e.currentTarget;
                            el.style.display = "none";
                            el.parentElement!.style.backgroundColor = brand.color;
                            el.parentElement!.innerHTML = `<span style="color:white;font-weight:900;font-size:1.1rem">${brand.logo}</span>`;
                          }}
                        />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg font-bold text-foreground">{brand.name}</h3>
                        <p className="text-sm text-muted-foreground mt-0.5 leading-snug">{brand.description}</p>
                      </div>
                      {brand.formula && (
                        <div className="hidden sm:block ml-auto shrink-0">
                          <span
                            className="px-3 py-1.5 rounded-lg text-xs font-mono font-semibold"
                            style={{ backgroundColor: pillBg(brand.color), color: brand.color }}
                          >
                            {brand.formula}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Mobile formula */}
                    {brand.formula && (
                      <div className="sm:hidden px-6 pt-4">
                        <SectionLabel>Fórmula</SectionLabel>
                        <span
                          className="inline-block px-3 py-1.5 rounded-lg text-xs font-mono font-semibold"
                          style={{ backgroundColor: `${brand.color}15`, color: brand.color }}
                        >
                          {brand.formula}
                        </span>
                      </div>
                    )}

                    <div className="p-6 space-y-7">
                      {/* 1. Model lines */}
                      <div className={`grid gap-6 ${brand.extraModelLines ? "sm:grid-cols-2" : ""}`}>
                        <div>
                          <SectionLabel>{brand.modelLinesNote ? "Cotxes" : "Models"}</SectionLabel>
                          <div className="divide-y divide-border/50">
                            {brand.modelLines.map((line) => (
                              <div key={line.code} className="flex items-start gap-3 py-2">
                                <span className="text-xs font-bold shrink-0 w-28 font-mono pt-0.5" style={{ color: brand.color }}>
                                  {line.code}
                                </span>
                                <span className="text-sm text-muted-foreground leading-snug">{line.desc}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {brand.extraModelLines && (
                          <div>
                            <SectionLabel>{brand.modelLinesNote}</SectionLabel>
                            <div className="divide-y divide-border/50">
                              {brand.extraModelLines.map((line) => (
                                <div key={line.code} className="flex items-start gap-3 py-2">
                                  <span className="text-xs font-bold shrink-0 w-28 font-mono pt-0.5" style={{ color: brand.color }}>
                                    {line.code}
                                  </span>
                                  <span className="text-sm text-muted-foreground leading-snug">{line.desc}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 2. Porsche hierarchy (before suffixes) */}
                      {brand.hierarchy && (
                        <div>
                          <SectionLabel>Jerarquia del 911 (potència creixent)</SectionLabel>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {brand.hierarchy.map((step, i) => (
                              <div key={step} className="flex items-center gap-1.5">
                                <span
                                  className="px-3 py-1.5 rounded-lg text-sm font-semibold"
                                  style={{
                                    backgroundColor: `${brand.color}${Math.round((isDark ? 28 : 15) + (i / (brand.hierarchy!.length - 1)) * 30).toString(16).padStart(2, "0")}`,
                                    color: brand.color,
                                    border: `1.5px solid ${brand.color}${Math.round((isDark ? 50 : 25) + (i / (brand.hierarchy!.length - 1)) * 40).toString(16).padStart(2, "0")}`,
                                  }}
                                >
                                  {step}
                                </span>
                                {i < brand.hierarchy!.length - 1 && (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground/40 shrink-0">
                                    <path d="m9 18 6-6-6-6" />
                                  </svg>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 3. Suffixes */}
                      {brand.suffixes && brand.suffixes.length > 0 && (
                        <div>
                          <SectionLabel>Sufixos clau</SectionLabel>
                          <div className="divide-y divide-border/50">
                            {brand.suffixes.map((suffix) => (
                              <div key={suffix.code} className="flex items-start gap-3 py-2">
                                <span className="text-xs font-bold shrink-0 w-28 font-mono pt-0.5" style={{ color: brand.color }}>
                                  {suffix.code}
                                </span>
                                <span className="text-sm text-muted-foreground leading-snug">{suffix.meaning}</span>
                              </div>
                            ))}
                          </div>
                          {brand.suffixWarning && (
                            <div className="flex gap-2 mt-3 p-3.5 bg-amber-500/10 dark:bg-amber-500/20 border border-amber-500/20 dark:border-amber-500/40 rounded-xl">
                              <WarningIcon />
                              <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">{brand.suffixWarning}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* 4. Examples */}
                      {brand.examples && brand.examples.length > 0 && (
                        <div>
                          <SectionLabel>Com llegir-lo</SectionLabel>
                          <div className="grid sm:grid-cols-2 gap-3">
                            {brand.examples.map((example) => (
                              <div key={example.model} className="bg-background border border-border rounded-xl p-5">
                                <div className="flex flex-wrap gap-1 mb-5">
                                  {example.parts.map((part, i) => (
                                    <span
                                      key={i}
                                      className="px-2 py-1 rounded-lg text-base sm:text-lg font-bold font-mono leading-none"
                                      style={{
                                        backgroundColor: chipBg(brand.color),
                                        color: brand.color,
                                        border: `1.5px solid ${chipBorder(brand.color)}`,
                                      }}
                                    >
                                      {part.text}
                                    </span>
                                  ))}
                                </div>
                                <div className="space-y-2">
                                  {example.parts.map((part, i) => (
                                    <div key={i} className="flex items-start gap-2.5">
                                      <span className="text-xs font-bold shrink-0 font-mono w-14 text-right leading-relaxed" style={{ color: brand.color }}>
                                        {part.text}
                                      </span>
                                      <span className="text-xs text-muted-foreground leading-relaxed">{part.label}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 5. Number note */}
                      {brand.numberNote && (
                        <div className="flex gap-3 p-4 bg-secondary/40 rounded-xl border border-border">
                          <InfoIcon />
                          <p className="text-sm text-muted-foreground leading-relaxed">{brand.numberNote}</p>
                        </div>
                      )}

                      {/* 6. Audi power table */}
                      {brand.powerTable && (
                        <div>
                          <SectionLabel>Codis de potència Audi</SectionLabel>
                          <div className="overflow-hidden rounded-xl border border-border">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-secondary/60">
                                  <th className="px-4 py-2.5 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider w-20">Codi</th>
                                  <th className="px-4 py-2.5 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Potència aproximada</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-border/50">
                                {brand.powerTable.map((row) => (
                                  <tr key={row.code} className="hover:bg-secondary/30 transition-colors">
                                    <td className="px-4 py-2.5">
                                      <span className="font-bold font-mono text-sm" style={{ color: brand.color }}>{row.code}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-muted-foreground">{row.cv}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* 7. Extra note */}
                      {brand.note && (
                        <div className="flex gap-3 p-4 bg-secondary/40 rounded-xl border border-border">
                          <InfoIcon />
                          <p className="text-sm text-muted-foreground leading-relaxed">{brand.note}</p>
                        </div>
                      )}
                    </div>
            </section>
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-12 text-center">
          <p className="text-xs text-muted-foreground/60">
            {allBrands.length} marques · Dades actuals a {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};
